import { autoinject, transient, BindingEngine } from "aurelia-framework";
import { RightShipInspectionModel, RightShipInspectionType } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { StepModel } from "../../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { UnitOfWorkAction, UnitOfWorkItem } from "../../../../../../utilities/unit-of-work-observer";
import { BaseValidationResponse } from "../../../../../../models/validations/validations.models";
import { Guid } from "../../../../../../utilities/guid";
import { Router } from "aurelia-router";
import { EventAggregator } from "aurelia-event-aggregator";
import { I18N } from "aurelia-i18n";
import {
    NotificationClientService,
    NotificationType
} from "../../../../../../services/basics/notification-client.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { BaseOfficeTimeBaseEntity } from "../../../common-steps/base-office-time";
import { ValidationControllerFactory, ValidationController, ValidationRules, validateTrigger } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { EmailValidator } from "../../../../../../utilities/email-validator";

@autoinject
@transient()
export class RequiresRightShipInspectionDialog extends BaseOfficeTimeBaseEntity<RightShipInspectionModel> {

    public alreadyExists: boolean;
    public vesselId: number;
    public incidentId: number;
    public pscInspectionId: number;
    public vesselName: string;
    public imo: string;
    public correspondenceMessage: string = "";
    public rightShipInspectionRequestSubmitted: boolean = false;
    public inspectionTypeId: number;
    public validator: ValidationController;
    public shipManagerEmails: string[] = [];

    constructor(private rightShipInspectionService: RightShipInspectionsService, private dialogClientService: DialogClientService, router: Router,
        bindingEngine: BindingEngine, i18N: I18N, notificationService: NotificationClientService, userClientService: UserClientService,
        eventAggregator: EventAggregator, validationsClientService: ValidationsClientService, validationControllerFactory: ValidationControllerFactory) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService);
        this.validator = validationControllerFactory.createForCurrentScope();
        this.validator.addRenderer(new CustomValidationRenderer());
        this.validator.changeTrigger(validateTrigger.manual);
    }

    public async activate(params: any, navigationInstruction: any): Promise<void> {
        this.pscInspectionId = params.pscInspectionId;
        this.incidentId = params.incidentId;
        this.vesselId = params.vesselId;
        this.vesselName = params.vesselName;
        this.imo = params.imo;
        this.shipManagerEmails = params.shipManagerEmails;
        this.inspectionTypeId = params.type ? params.type : RightShipInspectionType.RightShipInspection;
        params.isFromDialog = true;

        this.correspondenceMessage = `This message is sent as subject vessel has been marked for a RightShip inspection. An inspection request is entirely a matter for Owners where the following should be considered:
            <br>- Under current RightShip rules vessels over 8,000 DWT and 14 years of age, and all vessels over 25 years of age, must have a valid RightShip inspection for the vessel to be recommended to RightShip customers.
            <br>- Some RightShip customers have inspection requirements that exceed the standard RightShip rules (e.g irrespective of vessel’s age).
            <br>- Having a valid RightShip inspection badge, whether required by the above or not, may boost confidence in a vessel’s performance and upholding the industry safety standards. This will be lost at the time of inspection expiry.
            <br>An Inspection application can be submitted from the RightShip website by clicking on the button below.
            <br>`;

        const response = await this.rightShipInspectionService.getRequiredRightShipInspection(this.vesselId, this.inspectionTypeId, false);
        this.alreadyExists = response.hasPendingInspection || response.rightShipInspection != null;
        return super.activate(params, navigationInstruction);
    }

    attached() {
        this.setRules();
    }

    protected close() {
        this.dialogClientService.hide();
    }

    protected getListRoute(): string {
        return null;
    }

    protected getSteps(): StepModel[] {
        return [];
    }

    protected getItem(id: number): Promise<RightShipInspectionModel> {
        return new Promise((resolve, reject) => {
            this.rightShipInspectionService.getRightShipInspection(id).then(resolve).catch(reject);
        })
    }

    protected getItemId(): number {
        return this.item.rightShipInspectionId;
    }

    protected saveItem(id: number, changes: { [index: string]: UnitOfWorkItem; }): Promise<BaseValidationResponse> {
        changes["useMobileInspectionWorkflow"] = {
            "action": UnitOfWorkAction.Added,
            "oldValue": null,
            "value": true
        }      
        return this.rightShipInspectionService.saveRightShipInspection({
            id: id,
            changes: changes,
            notifyShipManager: this.item.notifyShipManager,
            correspondenceMessage: this.correspondenceMessage
        });
    }

    public async beforeSave(skipValidation: boolean, resolve: (isValid: boolean) => void) {
        const response = await this.rightShipInspectionService.getRequiredRightShipInspection(this.vesselId, this.inspectionTypeId, false);

        if(response.hasPendingInspection || response.rightShipInspection != null) {
            this.notificationService.show("There is already an inspection in the task backlog for this", "", NotificationType.Error);
            resolve(false)
            return;
        }
        
        this.validator.validate().then(r => {
            if (r.valid) {
                if (this.item.notifyShipManager && this.emails && this.emails.filter(x => x.email != null && x.email.trim() != '').length > 0) {
                    //this.item.correspondenceEmail = this.emails[0].email;
                    const validEmails = this.emails.filter(x => x.email != null && x.email.trim() != '')
                        //.slice(1) //filter out first email which would be set in the correspondence email
                        .map(x => x.email);
                    if (validEmails.length > 0) {
                        this.item.additionalEmailsToNotify = validEmails.join(';');
                    }
                }
                //if notifying ship manager is unchecked, should set the additionalEmailsToNotify back to null
                else {
                    this.item.additionalEmailsToNotify = null;
                }
                resolve(true);
            } else resolve(false);
        });
    }

    setRules() {
        ValidationRules
            .ensure<RequiresRightShipInspectionDialog, any>(x => x.emails)
            .required()
            .withMessage("At least 1 email required")
            .minItems(1)
            .withMessage("At least 1 email required")
            .when(() => this.item.notifyShipManager)
            .on(this);

        this.validator.addObject(this);
    }

    protected getUpdateRoute(): string {
        return null;
    }

    protected getEntityType(): EntityTypes {
        return EntityTypes.RightShipInspection;
    }

    protected handleUpdateCompleted(response: any, isNew: boolean) {
        this.rightShipInspectionRequestSubmitted = true;
        if (this._params.reload)
            this._params.reload();
        super.handleUpdateCompleted(response, isNew);
    }

    protected navigateToRightShipInspection() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.RightShipInspection, { id: this.item.rightShipInspectionId, source: "vessels" })
    }

    protected initializeItemForCreate(): RightShipInspectionModel | Promise<RightShipInspectionModel> {
        const item = <RightShipInspectionModel>{
            additionalEmailsToNotify: null,
            agentEmail: null,
            agentName: null,
            agentOfficeNumber: null,
            agentOfficeNumberWithoutCode: null,
            assignedToUserName: null,
            assignmentComment: null,
            hasVet: false,
            inspectionRequestComments: null,
            previouslyAssignedToUserName: null,
            rightShipInspectionId: 0,
            technicalManagerContactNumber: null,
            technicalManagerContactNumberWithoutCode: null,
            technicalManagerEmail: null,
            technicalManagerName: null,
            technicalManagmentCompany: null,
            correspondenceEmail: null,
            vesselId: 0,
            incidentId: null,
            pscInspectionId: null,
            vesselOwnerName: null,
            rightShipInspectionDeficiencies: [],
            invoices: [],
            agreeToSafetyScoreClause: false,
            agreeToTermsAndConditions: false,
            allDeficienciesReviewed: false,
            assignedDate: null,
            notifyShipManager: null,
            useMobileInspectionWorkflow: null,
            assignedTo: null,
            businessUnitId: null,
            cargoTypeId: null,
            countryId: null,
            currentRightShipInspectionStatusHistoryId: null,
            entityType: EntityTypes.RightShipInspection,
            estimatedArrivalDate: null,
            fullInspectionReportReviewed: null,
            identifier: Guid.generateGuid(),
            inspectingCompanyId: null,
            inspectorId: null,
            isVesselOwnerInformedAndAgreed: null,
            antiBriberyAndCorruptionPolicy: null,
            lengthOfTimeAtBerth: null,
            noLetterOfIndemnityWillBeSigned: false,
            bothOwnerAndManagerPromptlyReport: false,
            numberOfDeficiencies: 0,
            overallAssessmentId: null,
            rightShipInspectionTypeId: null,
            portId: null,
            previouslyAssignedTo: null,
            reasonForInspectionId: null,
            requestedDateOfInspection: null,
            actualDateOfInspection: null,
            inspectorContactNumber: null,
            inspectorEmail: null,
            berth: null,
            validityExpiryDate: null,
            requiresRightshipInspectionComments: null,
            rightShipInspectionVesselTypeId: null,
            isSelfUnloadingVessel: null,
            isUsingLngFuel: null,
            isEquippedWithGantryCranes: null,
            hasIceClassNotation: null,
            isGearlessVessel: null,
            isRoRoShip: null,
            isTimberCarrier: null,
            isCarryingGrainCargoAndHasBeenFumigatedInThePast12Months: null,
            captainName: null,
            captainEmail: null,
            isHybrid: false,
            isVesselInvolvedInSts: null,
        };

        return item;
    }

    protected initializeItemOverride() {
        this.item.rightShipInspectionTypeId = this.inspectionTypeId;
        this.item.vesselId = this.vesselId;
        this.item.pscInspectionId = this.pscInspectionId;
        this.item.incidentId = this.incidentId;
        this.item.notifyShipManager = true;
        
        if (this.shipManagerEmails && this.shipManagerEmails.length > 0) {
            this.shipManagerEmails.forEach(x => {
                this.addEmail(x);
            });
        }
        else {
            this.addEmail();
        }
    }

    protected initializeItemOverrideForUpdate() {
        this.item.rightShipInspectionTypeId = this.inspectionTypeId;
    }

    public notifyShipManagerChanged() {
        if (!this.emails || this.emails.length <= 0) return;
        if (this.item.notifyShipManager == false) {
            this.emails.forEach(x => {
                this.validator.removeObject(x);
            });
        }
        else {
            this.emails.forEach(x => {
                const rules = this.getEmailRules(x);
                this.validator.addObject(x, rules);
            });
        }
    }

    emails: {
        email: string
    }[] = [];

    removeEmail(index) {
        const deleted = this.emails.splice(index, 1);
        this.validator.removeObject(deleted[0]);
    }

    getEmailRules(email: { email: string }) {

        const rules = ValidationRules.ensure((x: { email: string }) => x.email)
            .satisfies(x => {
                return !(((x == null || x.trim() == '') && this.item.notifyShipManager));
            })
            .withMessage("Email Required")
            .satisfies(x => EmailValidator.isValidEmail(x))
            .on(email).rules;

        return rules;
    }

    addEmail(emailAddress = '') {
        const email = {
            email: emailAddress
        };
        this.emails.push(email);

        const rules = this.getEmailRules(email);
        this.validator.addObject(email, rules);
    }

    reasonsForRSInspectionLoaded($event) {
        if (this.item.reasonForInspectionId > 0) return;
        if ($event && $event.detail && $event.detail.values && Array.isArray($event.detail.values) && $event.detail.values.length > 0) {
            const values = $event.detail.values;

            if (this.incidentId && !this.item.reasonForInspectionId) {
                const valuesForDefault = values.filter(x => x.name.toLowerCase().trim().replace(/ /g, '_') == 'unsatisfactory_incident_investigation');
                if (valuesForDefault && valuesForDefault.length > 0)
                    this.item.reasonForInspectionId = valuesForDefault[0].id;
            }
            else if (this.pscInspectionId && !this.item.reasonForInspectionId) {
                const valuesForDefault = values.filter(x => x.name.toLowerCase().trim().replace(/ /g, '_') == 'unsatisfactory_psc_closeout');
                if (valuesForDefault && valuesForDefault.length > 0)
                    this.item.reasonForInspectionId = valuesForDefault[0].id;
            }
            else if (values.filter(x => x.isDefault == true).length > 0 && !this.item.reasonForInspectionId) {
                this.item.reasonForInspectionId = values.filter(x => x.isDefault == true)[0].id;
            }
            else if (!this.item.reasonForInspectionId) this.item.reasonForInspectionId = values[0].id;
        }
    }

}